import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, Lock, ArrowRight, ChevronLeft, Headphones, Mic, BookOpen, Pencil } from "lucide-react";
import { T, PrimaryBtn, NeonInput, HudFrame, AIPropsBackground, AuthBrand, useClickSound } from "@/pages/AuthPreviewShared";

/**
 * LOGIN PREVIEW — EnglishTest.UK
 * Daylight Light theme + solid orange button + click sound + AI props
 * Split-screen: brand+stats left, login form right
 */
export default function LoginPreview() {
  const play = useClickSound();
  useEffect(() => {
    const id = "lingoq-preview-fonts";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;600;700&family=IBM+Plex+Mono:wght@400;500;600&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  return (
    <div className="min-h-screen relative grid grid-cols-1 lg:grid-cols-[1.05fr_1fr]" style={{ background: T.bg, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="login-preview-root">
      <style>{`
        .display { font-family: 'Rajdhani', sans-serif; letter-spacing: -0.01em; }
        .mono    { font-family: 'IBM Plex Mono', monospace; }
      `}</style>

      {/* ============================================================
          LEFT — brand + welcome message + KPI cards + AI props
      ============================================================ */}
      <div className="relative overflow-hidden p-8 lg:p-14 hidden lg:flex flex-col justify-between"
        style={{ background: `linear-gradient(135deg, ${T.bg2} 0%, ${T.cream}88 100%)`, borderRight: `1px solid ${T.border}` }}>
        <AIPropsBackground />

        <div className="relative">
          <AuthBrand />
        </div>

        <div className="relative">
          <div className="mono text-[10px] tracking-[0.3em] mb-3" style={{ color: T.primary }}>// SYSTEM LOGIN</div>
          <h1 className="display text-5xl lg:text-6xl font-bold leading-[1.0]" style={{ color: T.text }}>
            Welcome back.<br />
            <span style={{ color: T.primary, textShadow: `0 0 22px ${T.primaryGlow}` }}>Examiners</span> are<br />ready when you are.
          </h1>
          <p className="text-base mt-5 max-w-md leading-relaxed" style={{ color: T.textMuted }}>
            Re-engage your training session. Pick up where you left off — every band, every skill, every drill is saved.
          </p>

          {/* Mini KPI strip */}
          <div className="mt-9 grid grid-cols-2 gap-3 max-w-md">
            {[
              { Icon: Headphones, l: "Last Listening", v: "Band 8.0" },
              { Icon: Pencil,     l: "Last Writing",   v: "Band 7.0" },
              { Icon: BookOpen,   l: "Last Reading",   v: "Band 7.5" },
              { Icon: Mic,        l: "Last Speaking",  v: "Band 8.5" },
            ].map((k, i) => (
              <HudFrame key={i} className="p-3" color={T.primary}>
                <div className="flex items-center gap-2 mono text-[9px] tracking-[0.25em]" style={{ color: T.textMuted }}>
                  <k.Icon className="w-3.5 h-3.5" style={{ color: T.primary }} /> {k.l.toUpperCase()}
                </div>
                <div className="display font-bold text-xl mt-1" style={{ color: T.text }}>{k.v}</div>
              </HudFrame>
            ))}
          </div>
        </div>

        <div className="relative mono text-[10px] tracking-[0.3em]" style={{ color: T.textMuted }}>
          <span style={{ color: T.success }}>{">"}</span> 27,400 candidates training right now
        </div>
      </div>

      {/* ============================================================
          RIGHT — login form
      ============================================================ */}
      <div className="relative flex items-center justify-center p-6 sm:p-10 lg:p-16">
        <div className="absolute inset-0 lg:hidden">
          <AIPropsBackground />
        </div>

        <div className="relative w-full max-w-md">
          {/* Mobile brand */}
          <div className="lg:hidden mb-8 flex items-center justify-between">
            <AuthBrand />
          </div>

          <Link to="/design-preview/landing" onClick={() => play("hover")}
            className="inline-flex items-center gap-1 mono text-[10px] tracking-[0.3em] hover:underline mb-6"
            style={{ color: T.textMuted }} data-testid="back-to-landing">
            <ChevronLeft className="w-3 h-3" /> BACK TO HOME
          </Link>

          <HudFrame color={T.primary} className="p-1">
            <div className="rounded-sm p-7 lg:p-9" style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}` }}>
              <div className="mono text-[10px] tracking-[0.3em] mb-2" style={{ color: T.success }}>// AUTHENTICATE</div>
              <h2 className="display text-4xl font-bold mb-2" style={{ color: T.text }}>Sign in</h2>
              <p className="mono text-xs mb-7" style={{ color: T.textMuted }}>Re-engage your training session.</p>

              <form onSubmit={(e) => e.preventDefault()} className="space-y-5">
                <NeonInput label="Email Address" icon={Mail} type="email" placeholder="alex@quickmail.com" data-testid="login-email" />
                <NeonInput label="Password" icon={Lock} type="password" placeholder="••••••••••" data-testid="login-password" />

                <div className="flex items-center justify-between mono text-[11px]">
                  <label className="inline-flex items-center gap-2 cursor-pointer" style={{ color: T.textMuted }}>
                    <input type="checkbox" className="w-4 h-4 rounded-sm cursor-pointer" style={{ accentColor: T.primary }} />
                    REMEMBER ME
                  </label>
                  <Link to="/design-preview/forgot" onClick={() => play("hover")} className="uppercase tracking-[0.25em]" style={{ color: T.primary }}>
                    FORGOT?
                  </Link>
                </div>

                <PrimaryBtn type="submit" className="w-full h-16 text-base" data-testid="login-submit">
                  AUTHENTICATE <ArrowRight className="w-5 h-5" />
                </PrimaryBtn>
              </form>

              <div className="my-7 flex items-center gap-3">
                <div className="flex-1 h-px" style={{ background: T.borderSoft }} />
                <div className="mono text-[10px] tracking-[0.3em]" style={{ color: T.textMuted }}>OR</div>
                <div className="flex-1 h-px" style={{ background: T.borderSoft }} />
              </div>

              <button
                onClick={() => play("primary")}
                data-testid="login-google"
                className="w-full inline-flex items-center justify-center gap-3 h-12 rounded-sm transition-all hover:bg-slate-50"
                style={{ background: T.cardBg, border: `2px solid ${T.border}`, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                <svg className="w-5 h-5" viewBox="0 0 48 48">
                  <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917" />
                  <path fill="#FF3D00" d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691" />
                  <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44" />
                  <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917" />
                </svg>
                <span className="text-sm font-semibold tracking-wide">Continue with Google</span>
              </button>

              <div className="text-sm text-center mt-7" style={{ color: T.textMuted, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                New to EnglishTest.UK?{" "}
                <Link to="/design-preview/signup" onClick={() => play("hover")} className="font-semibold" style={{ color: T.primary }}>
                  Create a free profile →
                </Link>
              </div>
            </div>
          </HudFrame>

          {/* Trust footer */}
          <div className="mt-6 flex items-center justify-between mono text-[9px] tracking-[0.3em]" style={{ color: T.textMuted }}>
            <span>// 256-BIT TLS · GDPR · SOC-2 READY</span>
            <span>v2.6 · UK</span>
          </div>
        </div>
      </div>
    </div>
  );
}
